import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Users, TrendingUp, Activity, FileText, 
  ArrowUp, ArrowDown, Download, RefreshCw, Search, 
  ChevronLeft, ChevronRight, Home, LogOut, Trash2
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#1F2A44', '#5A6A8A', '#D4B485', '#E6CB9F'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState({
    start: startOfDay(subDays(new Date(), 30)),
    end: endOfDay(new Date())
  });
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [stats, setStats] = useState(null);
  const [usersOverTime, setUsersOverTime] = useState([]);
  const [geoData, setGeoData] = useState({ countries: [], top_cities: [] });
  const [retentionData, setRetentionData] = useState([]);
  const [acquisitionData, setAcquisitionData] = useState([]);
  const [funnelData, setFunnelData] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const usersPerPage = 10;

  const handleToggleStatus = async (userId, currentStatus) => {
    if (!window.confirm(`Toggle user status from ${currentStatus} to ${currentStatus === 'active' ? 'inactive' : 'active'}?`)) return;
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.put(
        `${API}/admin/users/${userId}/toggle-status`,
        {},
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      
      toast.success(response.data.message);
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to toggle status:', error);
      toast.error('Failed to update user status');
    }
  };


  const handleFixSubscription = async (email, plan) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.put(`${API}/admin/fix-subscription`, { email, plan }, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      toast.success(`Updated ${email} to ${plan} plan`);
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      toast.error(`Failed: ${detail}`);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Permanently delete user ${userEmail} and all their data? This cannot be undone.`)) return;
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.delete(
        `${API}/admin/users/${userId}`,
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      
      toast.success('User deleted successfully');
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Failed to delete user');
    }
  };

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchDashboardData();
    }
  }, [dateRange, compareEnabled]);

  const checkAdminAccess = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) {
        navigate('/admin/login');
        return;
      }

      const response = await axios.get(`${API}/admin/verify`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (!response.data.is_admin) {
        toast.error('Admin access required');
        navigate('/admin/login');
      } else {
        setLoading(false);
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Admin check failed:', error);
      navigate('/admin/login');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { 'Authorization': `Bearer ${sessionToken}` };
      const params = {
        start_date: dateRange.start.toISOString(),
        end_date: dateRange.end.toISOString(),
        compare: compareEnabled
      };

      const [statsRes, usersRes, allUsersRes, geoRes, retentionRes, acquisitionRes, funnelRes] = await Promise.all([
        axios.get(`${API}/admin/metrics/overview`, { headers, params }),
        axios.get(`${API}/admin/metrics/users-over-time`, { headers, params }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/metrics/geographic`, { headers }),
        axios.get(`${API}/admin/metrics/retention`, { headers }),
        axios.get(`${API}/admin/metrics/acquisition`, { headers }),
        axios.get(`${API}/admin/metrics/funnel`, { headers })
      ]);

      setStats(statsRes.data);
      setUsersOverTime(usersRes.data.daily || []);
      setUsers(allUsersRes.data);
      setGeoData(geoRes.data);
      setRetentionData(retentionRes.data);
      setAcquisitionData(acquisitionRes.data);
      setFunnelData(funnelRes.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  const setPresetRange = (preset) => {
    const end = endOfDay(new Date());
    let start;
    
    switch (preset) {
      case 'today':
        start = startOfDay(new Date());
        break;
      case '7days':
        start = startOfDay(subDays(new Date(), 7));
        break;
      case '30days':
        start = startOfDay(subDays(new Date(), 30));
        break;
      case '90days':
        start = startOfDay(subDays(new Date(), 90));
        break;
      default:
        start = startOfDay(subDays(new Date(), 30));
    }
    
    setDateRange({ start, end });
  };

  const handleLogout = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('session_token');
      toast.success('Logged out');
      navigate('/admin/login');
    }
  };

  const handleExportCSV = async (type) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      let url = `${API}/admin/export/${type}`;
      
      if (type === 'signups') {
        url += `?start_date=${dateRange.start.toISOString()}&end_date=${dateRange.end.toISOString()}`;
      }

      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${sessionToken}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${type}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('CSV downloaded!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-navy-900 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--navy-50)' }}>
      <div className="max-w-[1600px] mx-auto p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Admin Analytics
            </h1>
            <p className="text-gray-600">BragBox Platform Insights</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Home className="w-4 h-4" />
              Back to App
            </Button>
          </div>
        </div>

        {/* Date Controls */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 mb-8">
          <div className="flex flex-wrap gap-3 items-center">
            <Button onClick={() => setPresetRange('today')} variant="outline" size="sm">Today</Button>
            <Button onClick={() => setPresetRange('7days')} variant="outline" size="sm">Last 7 days</Button>
            <Button onClick={() => setPresetRange('30days')} variant="outline" size="sm">Last 30 days</Button>
            <Button onClick={() => setPresetRange('90days')} variant="outline" size="sm">Last 90 days</Button>
            
            <label className="flex items-center gap-2 ml-4">
              <input
                type="checkbox"
                checked={compareEnabled}
                onChange={(e) => setCompareEnabled(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm">Compare to previous</span>
            </label>

            <Button onClick={fetchDashboardData} variant="outline" size="sm" className="ml-auto">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Metrics Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: 'var(--navy-900)' }}>
                    {stats.total_users}
                  </p>
                </div>
                <Users className="w-10 h-10" style={{ color: 'var(--navy-600)' }} />
              </div>
              {compareEnabled && stats.user_growth_percent !== undefined && (
                <div className="flex items-center gap-1">
                  {stats.user_growth_percent >= 0 ? <ArrowUp className="w-4 h-4 text-green-600" /> : <ArrowDown className="w-4 h-4 text-red-600" />}
                  <span className={stats.user_growth_percent >= 0 ? 'text-green-600 text-sm font-medium' : 'text-red-600 text-sm font-medium'}>
                    {Math.abs(stats.user_growth_percent).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-600">Active (7d)</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: 'var(--navy-900)' }}>
                    {stats.active_users_week}
                  </p>
                </div>
                <Activity className="w-10 h-10" style={{ color: 'var(--cream-600)' }} />
              </div>
              {compareEnabled && stats.active_growth_percent !== undefined && (
                <div className="flex items-center gap-1">
                  {stats.active_growth_percent >= 0 ? <ArrowUp className="w-4 h-4 text-green-600" /> : <ArrowDown className="w-4 h-4 text-red-600" />}
                  <span className={stats.active_growth_percent >= 0 ? 'text-green-600 text-sm font-medium' : 'text-red-600 text-sm font-medium'}>
                    {Math.abs(stats.active_growth_percent).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-600">Total Wins</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: 'var(--navy-900)' }}>
                    {stats.total_wins}
                  </p>
                </div>
                <TrendingUp className="w-10 h-10" style={{ color: 'var(--navy-600)' }} />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-600">Documents</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: 'var(--navy-900)' }}>
                    {stats.total_documents}
                  </p>
                </div>
                <FileText className="w-10 h-10" style={{ color: 'var(--cream-600)' }} />
              </div>
            </div>
          </div>
        )}

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                Users Over Time
              </h2>
              <Button
                onClick={() => handleExportCSV('signups')}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </Button>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={usersOverTime}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#1F2A44" strokeWidth={2} name="Users" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Acquisition Channels
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={acquisitionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={90}
                  dataKey="value"
                >
                  {acquisitionData.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Signups Over Time - Full Width */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Signups Over Time
            </h2>
            <Button
              onClick={() => handleExportCSV('signups')}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={usersOverTime}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#1F2A44" radius={[8, 8, 0, 0]} name="New Signups" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                Engagement Funnel
              </h2>
              <Button
                onClick={() => handleExportCSV('funnel')}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </Button>
            </div>
            <div className="space-y-4">
              {funnelData.map((stage, index) => (
                <div key={index}>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium" style={{ color: 'var(--navy-900)' }}>
                      {stage.stage}
                    </span>
                    <span className="text-sm text-gray-600">
                      {stage.users} ({stage.percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className="h-4 rounded-full"
                      style={{ width: `${stage.percentage}%`, backgroundColor: 'var(--navy-900)' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                Geographic Distribution
              </h2>
              <Button
                onClick={() => handleExportCSV('geographic')}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </Button>
            </div>
            {geoData.countries && geoData.countries.length > 0 ? (
              <div className="space-y-3">
                {geoData.countries.slice(0, 6).map((country, index) => (
                  <div key={index} className="flex justify-between items-center p-3 rounded" style={{ backgroundColor: 'var(--navy-50)' }}>
                    <span className="font-medium" style={{ color: 'var(--navy-900)' }}>{country.name}</span>
                    <span className="font-bold" style={{ color: 'var(--navy-900)' }}>{country.users} users</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-20">No location data yet</p>
            )}
          </div>
        </div>

        {/* Retention Cohorts */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              User Retention Cohorts
            </h2>
            <Button
              onClick={() => handleExportCSV('retention')}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>Cohort</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>Signups</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>Week 1</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>Week 2</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>Week 4</th>
                </tr>
              </thead>
              <tbody>
                {retentionData.length > 0 ? retentionData.map((cohort, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-3 px-4 font-medium" style={{ color: 'var(--navy-900)' }}>{cohort.cohort}</td>
                    <td className="py-3 px-4">{cohort.signups}</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded text-sm font-medium ${
                        cohort.week_1 > 50 ? 'bg-green-100 text-green-800' : 
                        cohort.week_1 > 25 ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'
                      }`}>
                        {cohort.week_1}%
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded text-sm font-medium ${
                        cohort.week_2 > 50 ? 'bg-green-100 text-green-800' : 
                        cohort.week_2 > 25 ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'
                      }`}>
                        {cohort.week_2}%
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded text-sm font-medium ${
                        cohort.week_4 > 50 ? 'bg-green-100 text-green-800' : 
                        cohort.week_4 > 25 ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'
                      }`}>
                        {cohort.week_4}%
                      </span>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="5" className="py-8 text-center text-gray-500">Not enough data yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* User Management */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              User Management ({filteredUsers.length})
            </h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                placeholder="Search users..."
                className="pl-10 w-64"
              />
            </div>
            <Button
              onClick={() => handleExportCSV('users-export')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export All Users
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>User</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>Company</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>Location</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>Wins</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>Docs</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>Joined</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>Plan</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>Status</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--navy-900)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user) => {
                  const userStatus = user.account_status || 'active';
                  const isActive = userStatus === 'active';
                  
                  return (
                    <tr key={user.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </td>
                      <td className="py-3 px-4 text-sm">{user.company || '-'}</td>
                      <td className="py-3 px-4 text-sm">
                        {user.city && user.country ? `${user.city}, ${user.country}` : user.country || 'Not set'}
                      </td>
                      <td className="py-3 px-4 font-medium">{user.total_wins || 0}</td>
                      <td className="py-3 px-4 font-medium">{user.total_documents || 0}</td>
                      <td className="py-3 px-4 text-sm">{new Date(user.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <select
                          value={user.subscription_plan || 'free'}
                          onChange={(e) => handleFixSubscription(user.email, e.target.value)}
                          className="text-xs px-2 py-1 border border-gray-300 rounded-md bg-white"
                          data-testid={`plan-select-${user.user_id}`}
                        >
                          <option value="free">Free</option>
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                          <option value="lifetime">Lifetime</option>
                        </select>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleToggleStatus(user.user_id, userStatus)}
                          className={`text-xs px-3 py-1 rounded-full font-medium cursor-pointer transition-colors hover:opacity-80 ${
                            isActive ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                          data-testid={`status-toggle-${user.user_id}`}
                        >
                          {isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleDeleteUser(user.user_id, user.email)}
                          className="text-red-600 hover:text-red-800 transition-colors p-2 hover:bg-red-50 rounded"
                          data-testid={`delete-user-${user.user_id}`}
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <p className="text-sm text-gray-600">
                Showing {((currentPage - 1) * usersPerPage) + 1}-{Math.min(currentPage * usersPerPage, filteredUsers.length)} of {filteredUsers.length}
              </p>
              <div className="flex gap-2">
                <Button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} variant="outline" size="sm">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-4 py-2 text-sm">Page {currentPage} of {totalPages}</span>
                <Button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} variant="outline" size="sm">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
